import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TableContainer,
  TextField,
  Tooltip,
} from '@mui/material'
import { Cancel, Error } from '@mui/icons-material'
import {
  DataGridPro,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { animalColsDF } from './AnimalColsDF'
import AnimalToolbar from './AnimalToolbar'
import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useGlobalContext } from '../../../Contexts/GlobalTriggers'
import { useUserContext } from '../../../Contexts/UserConfig'
import { useParams } from 'react-router-dom'
import { tsPyAnimal } from '../../../Typescript/StuartPy/PyAnimal'
import { useQueries } from 'react-query'
import AnimalService from '../../../APIs/Python/APIs/AnimalService'
import AnimalFooter from './AnimalFooter'
import { usePersistColumnSettings } from '../../../Functions/SourcedOnline/usePersistColumnSettings'
import { useStudyContext } from '../../../Contexts/Modules/StudyModule/SelectedStudyModule'
import EntryStepsHeader from './EntryStepsHeader'
import PageSection from '../../../Constants&Theme/PageSection'
import { stackGap } from '../../../Constants&Theme/Spacing'
import { usePyGet } from '../../../APIs/Hooks/PyAPIHooks'

export interface dfAnimal extends tsPyAnimal {
  PhaseID: string[]
  PhaseFilter: (string | null)[]
  StudyID: number[]
  PhaseName: (string | null)[]
}

function SelectAnimals() {
  const { playScanSound } = useGlobalContext()
  const { whoAmI } = useUserContext()
  const { studyQuery } = useStudyContext()
  const { data: study } = studyQuery

  const { study_id } = useParams()

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  )
  const [containers, setContainers] = useState<any[]>([])
  const [fetchContainers, setFetchContainers] = useState<any[]>([])
  const [showType, setShowType] = useState<'containers' | 'enrolled'>(
    'containers',
  )
  const [includeCulled, setIncludeCulled] = useState(false)
  const [inStudy, setInStudy] = useState(true)
  const [loadAll, setLoadAll] = useState(false)
  const [enrolled, setEnrolled] = useState<tsPyAnimal[]>([])
  const [filterPhase, setFilterPhase] = useState<undefined | string>(undefined)
  const [containerInput, setContainerInput] = useState('')
  const [selectedPhases, setSelectedPhases] = useState<(string | null)[]>([])

  //creating a PhaseID to PhaseName map
  const [phaseMap, setPhaseMap] = useState<any>({})
  useEffect(() => {
    const phaseMap: any = {}
    if (study?.phases) {
      study.phases.forEach(
        (phase) => (phaseMap[phase.PhaseID] = phase.PhaseName),
      )
    }
    setPhaseMap(phaseMap)
  }, [study])

  //store all scanned containers in local memory - prevent re-fetch on rescan / dupe
  useEffect(() => {
    setFetchContainers((f) => [...new Set([...f, ...containers])])
  }, [containers])

  //FETCHING Animals
  //Option 1) Enrolled Animals

  const enrolledAnimalQuery = usePyGet(
    ['/study', `/${study_id}`, `/animals?include_culled=${includeCulled}`],
    !!study_id && loadAll,
    undefined,
    {
      onSuccess: (data) => {
        setEnrolled(data.map((e: any) => e.Animal))
        //Clear selection
        setSelectionModel([])
      },
    },
  )

  //BUG causes infinite re-renders https://github.com/TanStack/query/issues/3049 https://github.com/TanStack/query/issues/2991
  //Cannot use useQueries response in useEffect dependency

  //Option 2) Animals in scanned containers
  const containerQueries = useQueries(
    fetchContainers.map((container_id) => {
      return {
        queryKey: ['AnimalService', 'getAnimalList', container_id],
        queryFn: () => AnimalService.getAnimalList(whoAmI, container_id),
      }
    }),
  )

  //clear containers if switching
  useEffect(() => {
    if (showType === 'enrolled') {
      setContainers([])
    }
  }, [showType])

  const errors = useMemo(
    () => containerQueries.filter((q) => q.status === 'error'),
    [containerQueries],
  )
  const errorContainers = errors.map((e: any) =>
    e.error.config.url.replace('/animal/container/', ''),
  ) //cannot find containerID in errorQuery directly
  const successful = useMemo(
    () => containerQueries.filter((q) => q.status === 'success'),
    [containerQueries],
  )
  const showAnimalsCalc = useMemo(() => {
    let temp: tsPyAnimal[] = []
    successful.forEach((s) => {
      temp = temp.concat(s.data)
    })
    return temp
  }, [successful])

  const containerLoading = useMemo(
    () => containerQueries.filter((q) => q.status === 'loading').length > 0,
    [containerQueries],
  )

  const showAnimals = useMemo(() => {
    const initAnimals = showType === 'containers' ? showAnimalsCalc : enrolled
    const mappedAnimals = initAnimals.map((a) => {
      return {
        ...a,
        PhaseID: a.Studies.filter((s) => s.StudyID === parseInt(study_id!)).map(
          (s) => s.PhaseID,
        ),
        StudyID: a.Studies.map((s) => s.StudyID),
        PhaseName: [],
        PhaseFilter: [],
      } as dfAnimal
    })
    return mappedAnimals.map((a) => {
      if (
        Object.keys(phaseMap).filter((value) => a.PhaseID.includes(value))
          .length > 0
      ) {
        return {
          ...a,
          PhaseName: a.PhaseID.map((p: string | number) =>
            phaseMap[p] ? phaseMap[p] : null,
          ),
          PhaseFilter: a.PhaseID.filter((p: string) =>
            Object.keys(phaseMap).includes(p),
          ),
        }
      } else {
        return { ...a, PhaseName: null, PhaseFilter: null }
      }
    })
  }, [showAnimalsCalc, enrolled, showType, phaseMap, study_id])

  //optional phase filter in Toolbar
  const filteredAnimals: any = useMemo(() => {
    let filtered = showAnimals
    if (inStudy) {
      filtered = filtered.filter((r) => r.StudyID.includes(parseInt(study_id!)))
    }
    if (filterPhase) {
      filtered = filtered.filter((r) => r.PhaseID.includes(filterPhase))
    }
    return filtered
  }, [filterPhase, showAnimals, inStudy, study_id])

  const apiRef = useGridApiRef()
  usePersistColumnSettings(apiRef, 'study-select-animals-grid')

  return (
    <Box
      sx={{
        width: 1,
        maxWidth: 'xl',
      }}
    >
      <EntryStepsHeader step={0}>
        <PageSection title={'Select Animals'}>
          <Stack
            gap={stackGap.col}
            width={1}
          >
            <Autocomplete
              sx={{ width: 1 }}
              multiple
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Containers"
                  placeholder="Container ID"
                  inputMode={'numeric'}
                />
              )}
              onChange={async (event, newValue, reason, details) => {
                setShowType('containers')
                switch (reason) {
                  case 'createOption':
                    if (details?.option) {
                      setContainers((c) => [...c, details.option])
                    }
                    break
                  case 'removeOption':
                  case 'clear':
                  default:
                    setContainers(newValue)
                }
              }}
              onInputChange={(event, newValue) => {
                setContainerInput(newValue)
              }}
              onKeyDown={(e: any) => {
                if (e.code === 'Tab') {
                  e.preventDefault()
                  setContainers((c) => [...c, e.target.value])
                  playScanSound(true)
                  setContainerInput('')
                }
              }}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Tooltip
                    key={index}
                    title={errorContainers.includes(option) ? 'Not Found' : ''}
                  >
                    <span>
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        deleteIcon={
                          errorContainers.includes(option) ?
                            <Error color={'error'} />
                          : <Cancel />
                        }
                      />
                    </span>
                  </Tooltip>
                ))
              }
              options={[]}
              getOptionLabel={(option) => option}
              freeSolo
              value={containers || []}
              inputValue={containerInput}
            />
            <TableContainer sx={{ width: 1 }}>
              <DataGridPro
                autoHeight
                pagination
                checkboxSelection
                rows={filteredAnimals.map((a: { UAI: any }) => {
                  return { ...a, id: a.UAI }
                })}
                onRowSelectionModelChange={(newSelectionModel: GridRowId[]) => {
                  if (filteredAnimals) {
                    setSelectedPhases(() => {
                      const selectedPhases: (string | null)[] = []
                      newSelectionModel.forEach((id: GridRowId) => {
                        const phaseID = filteredAnimals.find(
                          (a: { UAI: GridRowId }) => a.UAI === id,
                        )?.PhaseFilter
                        if (phaseID) {
                          phaseID.forEach((id: string | null) => {
                            if (id && !selectedPhases.includes(id)) {
                              selectedPhases.push(id)
                            }
                          })
                        } else {
                          if (!selectedPhases.includes(null)) {
                            selectedPhases.push(null)
                          }
                        }
                      })
                      return selectedPhases
                    })
                  }
                  setSelectionModel(newSelectionModel)
                }}
                rowSelectionModel={selectionModel}
                columns={animalColsDF}
                apiRef={apiRef}
                slots={{
                  footer: AnimalFooter as any,
                  toolbar: AnimalToolbar as any,
                }}
                slotProps={{
                  footer: {
                    selectionModel: selectionModel,
                    filteredAnimals: filteredAnimals,
                  } as any, //FIXME: https://github.com/mui/mui-x/issues/9775,
                  toolbar: {
                    selectionModel: selectionModel,
                    filterPhase: filterPhase,
                    setFilterPhase: setFilterPhase,
                    inStudy: inStudy,
                    setInStudy: setInStudy,
                    setLoadAll: setLoadAll,
                    setShowType: setShowType,
                    selectedPhases: selectedPhases,
                    showType: showType,
                    containerQueries: containerQueries,
                    includeCulled: includeCulled,
                    setIncludeCulled: setIncludeCulled,
                  },
                }}
                density={'compact'}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'AnimalID', sort: 'asc' }],
                  },
                  columns: {
                    columnVisibilityModel: {
                      ProtocolID: false,
                      PhaseID: false,
                    },
                  },
                }}
                loading={containerLoading || enrolledAnimalQuery.isLoading}
              />
            </TableContainer>
          </Stack>
        </PageSection>
      </EntryStepsHeader>
    </Box>
  )
}

export default SelectAnimals
